// ABOUTME: Billing store for subscription checkout, portal sessions, and credit management.
// Wraps billing API calls with loading state and Stripe redirect handling.

import { create } from 'zustand'
import { appUrl } from '@/config/appBaseUrl'
import { api } from '@/api/client'
import type { CreditUsage } from '@/types'

interface SubscriptionInfo {
  id: string
  // Both null on a trial subscription — a self-serve signup has no
  // Stripe subscription and no Stripe customer (backend #1922).
  stripe_subscription_id: string | null
  stripe_customer_id: string | null
  tier: string
  status: string
  current_period_start: string | null
  current_period_end: string | null
  trial_end: string | null
  cancel_at: string | null
  canceled_at: string | null
  is_active: boolean
  created_at: string
  updated_at: string
}

interface NoSubscriptionInfo {
  message: string
  tier: 'trial'
}

type SubscriptionState = SubscriptionInfo | NoSubscriptionInfo | null

interface CheckoutResponse {
  session_id: string
  url: string
}

interface PortalResponse {
  url: string
}

// Backend returns snake_case (app/schemas/billing.py:RedeemPromoResponse)
export interface PromoRedemption {
  code: string
  tier_granted: string
  credits_granted: number
  message: string
}

function isActiveSubscription(sub: SubscriptionState): sub is SubscriptionInfo {
  return sub !== null && 'stripe_subscription_id' in sub
}

interface BillingState {
  // State
  subscription: SubscriptionState
  creditBalance: CreditUsage | null
  isLoading: boolean
  isCheckoutLoading: boolean
  isPortalLoading: boolean
  isRedeemingPromo: boolean
  promoRedemption: PromoRedemption | null
  promoError: string | null
  error: string | null

  // Computed
  hasActiveSubscription: () => boolean
  currentTier: () => string

  // Actions
  fetchSubscription: () => Promise<void>
  fetchCreditBalance: () => Promise<void>
  applyBalance: (balance: number) => void
  createCheckout: (priceId: string) => Promise<void>
  openPortal: () => Promise<void>
  redeemPromo: (code: string) => Promise<boolean>
  reset: () => void
}

export const useBillingStore = create<BillingState>((set, get) => ({
  // Initial state
  subscription: null,
  creditBalance: null,
  isLoading: false,
  isCheckoutLoading: false,
  isPortalLoading: false,
  isRedeemingPromo: false,
  promoRedemption: null,
  promoError: null,
  error: null,

  // Computed
  hasActiveSubscription: () => {
    const sub = get().subscription
    return isActiveSubscription(sub)
  },

  currentTier: () => {
    const sub = get().subscription
    if (!sub) return 'trial'
    if ('tier' in sub) return sub.tier
    return 'trial'
  },

  // Actions
  fetchSubscription: async () => {
    set({ isLoading: true, error: null })
    try {
      const response = await api.get<SubscriptionInfo | NoSubscriptionInfo>('/billing/subscription')
      set({ subscription: response as unknown as SubscriptionState, isLoading: false })
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch subscription'
      set({ error: message, isLoading: false })
    }
  },

  fetchCreditBalance: async () => {
    try {
      const response = await api.get<CreditUsage>('/billing/credits')
      set({ creditBalance: response as CreditUsage })
    } catch {
      // Non-fatal — credit balance is supplementary
    }
  },

  // Merge an authoritative balance from the backend (e.g. the post-turn
  // credits_remaining on a chat SSE event) into the one shared creditBalance.
  // Every surface that shows a balance subscribes to this single value — the
  // top bar and the chat header rendering two different numbers at the same
  // moment was two independently-updated copies (#2191).
  applyBalance: (balance: number) => {
    const current = get().creditBalance
    if (current) {
      set({ creditBalance: { ...current, balance } })
    } else {
      // No snapshot yet to merge into — fetch the full record instead.
      void get().fetchCreditBalance()
    }
  },

  createCheckout: async (priceId: string) => {
    set({ isCheckoutLoading: true, error: null })
    try {
      const response = await api.post<CheckoutResponse>('/billing/checkout', {
        price_id: priceId,
        success_url: appUrl('/billing/success'),
        cancel_url: appUrl('/billing/cancel'),
      })
      const data = response as unknown as CheckoutResponse
      if (data.url) {
        window.location.href = data.url
      }
    } catch (err) {
      const message =
        err && typeof err === 'object' && 'message' in err
          ? (err as { message: string }).message
          : 'Failed to create checkout session'
      set({ error: message, isCheckoutLoading: false })
    }
  },

  openPortal: async () => {
    set({ isPortalLoading: true, error: null })
    try {
      const response = await api.post<PortalResponse>('/billing/portal', {
        return_url: appUrl('/settings/plan#plan'),
      })
      const data = response as unknown as PortalResponse
      if (data.url) {
        window.location.href = data.url
      }
    } catch (err) {
      const message =
        err && typeof err === 'object' && 'message' in err
          ? (err as { message: string }).message
          : 'Failed to open billing portal'
      set({ error: message, isPortalLoading: false })
    }
  },

  redeemPromo: async (code: string) => {
    set({ isRedeemingPromo: true, promoError: null, promoRedemption: null })
    try {
      const response = await api.post<PromoRedemption>('/billing/redeem-promo', { code })
      const data = response as unknown as PromoRedemption
      set({ promoRedemption: data, isRedeemingPromo: false })
      // The grant changed the tier and the credit balance — refresh both so
      // the page reflects it without a reload.
      await Promise.all([get().fetchSubscription(), get().fetchCreditBalance()])
      return true
    } catch (err) {
      const message =
        err && typeof err === 'object' && 'message' in err
          ? (err as { message: string }).message
          : "That promo code couldn't be redeemed"
      set({ promoError: message, isRedeemingPromo: false })
      return false
    }
  },

  reset: () => {
    set({
      subscription: null,
      creditBalance: null,
      isLoading: false,
      isCheckoutLoading: false,
      isPortalLoading: false,
      isRedeemingPromo: false,
      promoRedemption: null,
      promoError: null,
      error: null,
    })
  },
}))
